import {
  SequenceArrowKind,
  SequenceEventKind,
  SequenceNotePlacement,
  type MermaidSequence,
  type MermaidSequenceBlock,
  type MermaidSequenceEvent,
  type MermaidSequenceMessage,
  type MermaidSequenceNote,
  type MermaidSequenceParticipant,
} from './sequence-types';

/**
 * Hand-rolled parser for a Mermaid `sequenceDiagram`, matching the approach of
 * the sibling Mermaid renderers (no `mermaid` npm dependency — they all parse a
 * pragmatic subset by hand). Returns `null` for input that is not a usable
 * sequence diagram so the dispatcher in `Cmarkdown.tsx` can fall back to a
 * plain code block instead of rendering an empty widget.
 *
 * Supported subset:
 *   - `participant A` / `participant A as Alice` / `actor A [as Alice]`
 *   - messages: `A->B`, `A->>B`, `A-->B`, `A-->>B` with `: message` text
 *   - `Note over A,B: ...`, `Note left of A: ...`, `Note right of A: ...`
 *   - grouping blocks: `loop` / `alt` / `opt` / `par` ... `end`
 *
 * Anything else (activations `+`/`-`, `else` dividers, autonumber, etc.) is
 * skipped gracefully rather than aborting the parse.
 */

const EMPTY = '';
const COMMENT_TOKEN = '%%';
const ALIAS_KEYWORD = 'actor';
const PLACEMENT_LEFT = 'left of';
const PLACEMENT_RIGHT = 'right of';

interface ArrowToken {
  token: string;
  kind: SequenceArrowKind;
}

const ARROW_TOKENS: ReadonlyArray<ArrowToken> = [
  // Order matters: longer / dashed tokens first so `-->>` is matched before `->`.
  { token: '-->>', kind: SequenceArrowKind.Dashed },
  { token: '->>', kind: SequenceArrowKind.Solid },
  { token: '-->', kind: SequenceArrowKind.DashedOpen },
  { token: '->', kind: SequenceArrowKind.SolidOpen },
];

const BLOCK_OPENERS = new Set<string>([
  'loop',
  'alt',
  'opt',
  'par',
  'critical',
  'break',
]);

interface SequenceParserState {
  participants: MermaidSequenceParticipant[];
  seen: Set<string>;
  rootEvents: MermaidSequenceEvent[];
  stack: MermaidSequenceEvent[][];
  title?: string;
}

interface RegisterParticipantsInput {
  state: SequenceParserState;
  parts: string[];
}

interface SequenceLineHandlerInput {
  line: string;
  state: SequenceParserState;
}

interface ParseSequenceLinesInput {
  lines: string[];
  startIndex: number;
}

type SequenceLineHandler = (input: SequenceLineHandlerInput) => boolean;

/** Strip a trailing line comment (`%% ...`) and surrounding whitespace. */
function clean(line: string): string {
  const commentIdx = line.indexOf(COMMENT_TOKEN);
  const withoutComment = commentIdx >= 0 ? line.slice(0, commentIdx) : line;
  return withoutComment.trim();
}

/**
 * Normalize a participant reference token. Mermaid allows activation markers
 * directly adjacent to the participant id (`A->>+B`, `B-->>-A`); strip them.
 */
function normalizeRef(raw: string): string {
  return raw.trim().replace(/^[+-]/, EMPTY).replace(/[+-]$/, EMPTY).trim();
}

/** Lazily register a participant the first time it is referenced by a message. */
function ensureParticipant(
  id: string,
  participants: MermaidSequenceParticipant[],
  seen: Set<string>,
): void {
  const trimmed = id.trim();
  if (trimmed.length === 0 || seen.has(trimmed)) {
    return;
  }
  seen.add(trimmed);
  participants.push({ id: trimmed, label: trimmed, isActor: false });
}

/** Parse a `participant`/`actor` declaration line. */
function parseParticipantDeclaration(line: string): MermaidSequenceParticipant | null {
  const match = /^(participant|actor)\s+(.+)$/i.exec(line);
  if (!match) {
    return null;
  }
  const isActor = match[1].toLowerCase() === ALIAS_KEYWORD;
  const rest = match[2].trim();
  const aliasMatch = /^(.+?)\s+as\s+(.+)$/i.exec(rest);
  if (aliasMatch) {
    const id = aliasMatch[1].trim();
    const label = aliasMatch[2].trim();
    return { id, label: label.length > 0 ? label : id, isActor };
  }
  return { id: rest, label: rest, isActor };
}

function resolvePlacement(raw: string): SequenceNotePlacement {
  if (raw === PLACEMENT_LEFT) {
    return SequenceNotePlacement.Left;
  }
  if (raw === PLACEMENT_RIGHT) {
    return SequenceNotePlacement.Right;
  }
  return SequenceNotePlacement.Over;
}

/** Parse a `Note over/left of/right of ...: text` line. */
function parseNote(line: string): MermaidSequenceNote | null {
  const match = /^note\s+(over|left of|right of)\s+(.+?):\s*(.*)$/i.exec(line);
  if (!match) {
    return null;
  }
  const placement = resolvePlacement(match[1].toLowerCase());
  const participants = match[2]
    .split(',')
    .map((p) => normalizeRef(p))
    .filter((p) => p.length > 0);
  if (participants.length === 0) {
    return null;
  }
  return {
    kind: SequenceEventKind.Note,
    participants,
    placement,
    text: match[3].trim(),
  };
}

/** Parse a message line (`A ->> B : text`). Returns null if no arrow token. */
function parseMessage(line: string): MermaidSequenceMessage | null {
  for (const { token, kind } of ARROW_TOKENS) {
    const idx = line.indexOf(token);
    if (idx < 0) {
      continue;
    }
    const from = normalizeRef(line.slice(0, idx));
    const rhs = line.slice(idx + token.length);
    const colonIdx = rhs.indexOf(':');
    const to = normalizeRef(colonIdx >= 0 ? rhs.slice(0, colonIdx) : rhs);
    const text = colonIdx >= 0 ? rhs.slice(colonIdx + 1).trim() : EMPTY;
    if (from.length === 0 || to.length === 0) {
      return null;
    }
    return { kind: SequenceEventKind.Message, from, to, arrow: kind, text };
  }
  return null;
}

function getSequenceLines(source: string): string[] {
  return source
    .split('\n')
    .map((line) => clean(line))
    .filter((line) => line.length > 0);
}

function createSequenceParserState(): SequenceParserState {
  const rootEvents: MermaidSequenceEvent[] = [];
  return {
    participants: [],
    seen: new Set<string>(),
    rootEvents,
    stack: [rootEvents],
  };
}

function registerParticipants({
  state,
  parts,
}: RegisterParticipantsInput): void {
  for (const part of parts) {
    ensureParticipant(part, state.participants, state.seen);
  }
}

function getCurrentEvents(state: SequenceParserState): MermaidSequenceEvent[] {
  return state.stack[state.stack.length - 1] ?? state.rootEvents;
}

function applyTitleLine({ line, state }: SequenceLineHandlerInput): boolean {
  const titleMatch = /^title\s+(.+)$/i.exec(line);
  if (!titleMatch) {
    return false;
  }

  state.title = titleMatch[1].trim();
  return true;
}

function applyParticipantLine({
  line,
  state,
}: SequenceLineHandlerInput): boolean {
  const declaration = parseParticipantDeclaration(line);
  if (!declaration) {
    return false;
  }

  if (!state.seen.has(declaration.id)) {
    state.seen.add(declaration.id);
    state.participants.push(declaration);
  }
  return true;
}

function applyBlockCloseLine({
  line,
  state,
}: SequenceLineHandlerInput): boolean {
  if (!/^end\b/i.test(line)) {
    return false;
  }

  if (state.stack.length > 1) {
    state.stack.pop();
  }
  return true;
}

function applyElseDividerLine({ line }: SequenceLineHandlerInput): boolean {
  return /^else\b/i.test(line);
}

function applyBlockOpenLine({
  line,
  state,
}: SequenceLineHandlerInput): boolean {
  const blockMatch = /^(\w+)\b\s*(.*)$/i.exec(line);
  if (!blockMatch || !BLOCK_OPENERS.has(blockMatch[1].toLowerCase())) {
    return false;
  }

  const block: MermaidSequenceBlock = {
    kind: SequenceEventKind.Block,
    type: blockMatch[1].toLowerCase(),
    label: blockMatch[2].trim(),
    events: [],
  };
  getCurrentEvents(state).push(block);
  state.stack.push(block.events);
  return true;
}

function applyNoteLine({ line, state }: SequenceLineHandlerInput): boolean {
  const note = parseNote(line);
  if (!note) {
    return false;
  }

  registerParticipants({ state, parts: note.participants });
  getCurrentEvents(state).push(note);
  return true;
}

function applyMessageLine({ line, state }: SequenceLineHandlerInput): boolean {
  const message = parseMessage(line);
  if (!message) {
    return false;
  }

  registerParticipants({ state, parts: [message.from, message.to] });
  getCurrentEvents(state).push(message);
  return true;
}

const SEQUENCE_LINE_HANDLERS: ReadonlyArray<SequenceLineHandler> = [
  applyTitleLine,
  applyParticipantLine,
  applyBlockCloseLine,
  applyElseDividerLine,
  applyBlockOpenLine,
  applyNoteLine,
  applyMessageLine,
];

function applySequenceLine(input: SequenceLineHandlerInput): void {
  for (const handler of SEQUENCE_LINE_HANDLERS) {
    if (handler(input)) {
      return;
    }
  }
}

function parseSequenceLines({
  lines,
  startIndex,
}: ParseSequenceLinesInput): SequenceParserState {
  const state = createSequenceParserState();
  for (let lineIndex = startIndex; lineIndex < lines.length; lineIndex += 1) {
    applySequenceLine({ line: lines[lineIndex], state });
  }
  return state;
}

export function parseMermaidSequence(source: string): MermaidSequence | null {
  if (typeof source !== 'string' || source.trim().length === 0) {
    return null;
  }

  const lines = getSequenceLines(source);
  // The header keyword must be `sequenceDiagram` (optionally followed by config).
  const headerIdx = lines.findIndex((line) => /^sequence(diagram)?\b/i.test(line));
  if (headerIdx === -1) {
    return null;
  }

  const state = parseSequenceLines({ lines, startIndex: headerIdx + 1 });

  // A usable diagram needs at least one participant and one event.
  if (state.participants.length === 0 || !hasRenderableEvent(state.rootEvents)) {
    return null;
  }

  return {
    title: state.title,
    participants: state.participants,
    events: state.rootEvents,
  };
}

/** True if the event tree contains at least one message or note. */
function hasRenderableEvent(events: MermaidSequenceEvent[]): boolean {
  return events.some((event) => {
    if (event.kind === SequenceEventKind.Block) {
      return hasRenderableEvent(event.events);
    }
    return true;
  });
}
